/**
 * My Apps library — apps the user has added from the Store.
 *
 * Two backing stores, chosen per call via the module's current auth session
 * (see `setLibrarySession`):
 *   - ANONYMOUS: localStorage only, exactly as before this file grew a
 *     server mode — every successful run-create records its source here, and
 *     store-ref sources (`publisher/slug`) are enriched with the capsule's
 *     display name + icon from `GET /v1/capsules/by/:publisher/:slug`.
 *   - SIGNED IN: the server-side library (ato-api#276,
 *     `GET/POST/DELETE /v1/account/library*`) is authoritative for sources it
 *     can actually resolve — a ULID or `publisher/slug` ref (ato-api's
 *     `resolveLibrarySource` → `capsule_ref.ts`'s `parseCapsuleRef`, read
 *     directly as this lane's reference; see `isStoreRef`/`isUlidLike`).
 *     Reads/writes for THOSE sources go over the wire; name/icon arrive
 *     pre-joined from the server, so `enrichLibraryApp` is a no-op in this
 *     mode. A GitHub URL or `capsule://` ref has neither shape and would 400
 *     (`invalid_source`) if POSTed, so those sources stay LOCAL-ONLY even
 *     while signed in — but NOT in the same shared/unscoped storage as anon
 *     mode: they live in a key scoped by this account's user id
 *     (`accountLocalKey`), so a browser shared by two different signed-in
 *     accounts never leaks one account's local-fallback entries into the
 *     other's view (round-2 finding, reviewer-S / reviewer-fresh-r1). They
 *     surface through the local-overlay merge below rather than a doomed
 *     network write silently vanishing on reload.
 *     A one-time merge-import (`POST /library/import`) folds this browser's
 *     PRE-SIGNIN, unscoped localStorage entries into the signed-in user's
 *     server library. This is an EXPLICIT, opt-in action
 *     (`importLocalLibraryToAccount`) — NOT a side effect of signing in.
 *     Per RFC ato-api#261 §5, "Save to My Apps" and "Continue this session"
 *     are always separate operations: a bare sign-in (the same path a
 *     "Continue this session" preview flows through) must NEVER sweep this
 *     browser's anonymous usage history into the account's durable,
 *     cross-device library (round-5 finding D), and must never strand the
 *     anonymous view's entries with no way back if an import never confirms
 *     (round-5 finding C). So `setLibrarySession` deliberately does NOT run
 *     the import; only an explicit save action does (INTERIM: no UI wires
 *     that yet — it is ato-api#267's later lane — so in production this
 *     import currently never runs, and an ordinary sign-in leaves the
 *     anonymous library untouched).
 *
 *     When the explicit import DOES run, the claim — moving every
 *     non-Store-tile entry out of the shared key into THIS account's own
 *     local-fallback key (`accountLocalKey`) — happens synchronously and up
 *     front, before the import network call, so a DIFFERENT account running
 *     its OWN explicit import on the same browser afterward (even mid-race,
 *     before the first account's import POST has resolved) can never read or
 *     re-send data a previous account already claimed (round-2 finding,
 *     reviewer-S / reviewer-fresh-r1; round-4 finding, reviewer-S /
 *     reviewer-fresh-r3). The claimed copy is only actually deleted once the
 *     server CONFIRMS the whole sent batch merged (`imported === true &&
 *     skipped_count === 0`). On `imported: false` (another device already
 *     consumed this account's one-shot import guard), the local fallback is
 *     RECONCILED against the current authoritative GET and any server-
 *     resolvable entry the server no longer lists is dropped, so an app
 *     removed server-side does not resurrect as a ghost tile (round-5
 *     finding A; ato-api#276 close condition 3). A network error, other
 *     non-2xx, or a partial `skipped_count > 0` still leaves entries in
 *     place, visible via the local overlay, rather than guessing which are
 *     safe to destroy (round-4 finding, reviewer-T / reviewer-U).
 *
 * Every exported function keeps its EXACT pre-existing signature — callers
 * (App.tsx, DashboardApps, GetRun, OpenCapsule) do not need to change how they
 * call this module, only `setLibrarySession` is new, wired once from App.tsx's
 * account-state setter.
 *
 * Cross-repo note: the server routes this module calls exist only on
 * ato-api's `auth/my-apps-276` branch (PR #280) as of this writing — still
 * OPEN/DRAFT, not merged to `main` (round-4 finding, reviewer-T /
 * reviewer-fresh-r3). Deploying this lane must be paired with (never ahead
 * of) that API PR landing; until then every signed-in save 404s, is
 * swallowed by the fire-and-forget `.catch` handlers below, and silently
 * reverts on the next reload (see `loadServerLibrary`'s doc comment). To make
 * the SAFE state the default, server-backed My Apps is OPT-IN and DISABLED
 * unless `VITE_MY_APPS_SERVER_LIBRARY_ENABLED=true` is set explicitly (see
 * `serverLibraryEnabled`, round-5 finding B): a human flips it on once — and
 * only once — PR #280 is confirmed deployed to the target environment. Until
 * then every signed-in session behaves as pure local-storage mode, so no
 * `/v1/account/library*` call is ever attempted and there is nothing to 404.
 */

import type { AccountSession } from "../auth/accountTypes";
import { accountApiBase, fetchWithConnectionAuth } from "../app/connection";

export interface LibraryApp {
  /** The runnable source ref, e.g. `community/hello-capsule`. */
  source: string;
  /** Display name; defaults to the slug part until enriched. */
  name: string;
  /** Store icon URL, when the capsule has one. */
  iconUrl: string | null;
  addedAt: string;
  lastRunAt: string;
  /**
   * Saved launch settings — a relaunch placement HINT recorded when the
   * first confirmed launch reached ready (Install = library membership;
   * the binding is the optional fast-relaunch extra). Absent on legacy
   * entries and after invalidation. See SavedLaunchBinding.
   */
  binding?: SavedLaunchBinding | null;
}

/**
 * SavedLaunchBinding — a client-side placement hint for relaunching a
 * capsule without re-confirming. It is NOT an execution contract, NOT a
 * LaunchTemplate, NOT an ExecutionId, and NOT proof the saved launch is
 * still valid: Execution Identity / ExecutionGraph / Receipt /
 * LaunchTemplate / LaunchMaterializationRecord have load-bearing meanings
 * in the Rust launch lifecycle, and the PWA must not mirror them. The
 * consent / payment / trust / runner gates at POST /v1/runs remain the
 * real contract check; drift surfaces there and the launch flow falls
 * back to compute+confirm.
 *
 * `local` placements are deliberately not saveable in v1: cloud-mode
 * makes "no local runtime" a normal state, and the PWA
 * cannot verify a desktop companion anyway.
 */
export interface SavedLaunchBinding {
  schemaVersion: 1;
  sourceKey: string;
  placement: {
    /** Wire enums of POST /v1/runs — never a third client-only value. */
    kind: "managed-cloud" | "external-runner";
    runnerId?: string | null;
    runnerCompatibilityClass?: string | null;
  };
  capsuleScopedId?: string | null;
  /** The approved E302 consent_ref observed on the recording launch.
   *  Reference only — the canonical record is the runner-host ledger. */
  consentRef?: string | null;
  recordedAt: string;
}

const LIBRARY_KEY = "ato_app_library";
const MAX_APPS = 48;

function apiBase(): string {
  return accountApiBase();
}

/** Store refs are bare `publisher/slug` — one of the two forms the server
 *  library resolves (see `isUlidLike` for the other). */
export function isStoreRef(source: string): boolean {
  return /^[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9-]*$/.test(source.trim());
}

/** Bare-ULID capsule ref — 26-char Crockford base32, case-insensitive.
 *  MUST match ato-api's `isUlidLike` exactly (services/capsule_ref.ts, read
 *  directly as this lane's reference): `parseCapsuleRef` treats this shape as
 *  `kind: "id"` and resolves it directly, so a private/unlisted capsule
 *  reached via a bare-ID ref (e.g. `/open/<id>`) is just as server-resolvable
 *  as a `publisher/slug` ref. Round-2 finding (reviewer-U): the server-write
 *  gate below previously used `isStoreRef` alone, silently routing this
 *  entire source class to the local-only fallback despite the server
 *  actually accepting it. */
const ULID_REGEX = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
export function isUlidLike(source: string): boolean {
  return ULID_REGEX.test(source.trim());
}

/** Can the server library resolve this source at all? Gates every
 *  server-write path below (touch / record binding / invalidate / update
 *  consent ref) between "goes over the wire" and "local-fallback only". */
function isServerResolvable(source: string): boolean {
  return isStoreRef(source) || isUlidLike(source);
}

function slugOf(source: string): string {
  const parts = source.split("/");
  return parts[parts.length - 1] || source;
}

/** The retired Store tile, as earlier versions persisted it. Browsers that
 *  ran those versions still hold the entry, so every read drops it and the
 *  next write persists its absence — nothing else would ever remove it. */
function isRetiredStoreTile(app: LibraryApp): boolean {
  return (
    app.source === "store" ||
    (app as { kind?: string }).kind === "store"
  );
}

/** Raw localStorage read — the anonymous-mode source of truth (default key),
 *  and (when signed in) the local half of the overlay `loadLibrary()`
 *  composes with the server list, via an explicit account-scoped `key` (see
 *  `accountLocalKey`). Never call this directly from a consumer; it stays
 *  internal so every read goes through the mode-aware `loadLibrary()`. */
function loadLibraryLocal(key: string = LIBRARY_KEY): LibraryApp[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is LibraryApp =>
          Boolean(e) &&
          typeof (e as LibraryApp).source === "string" &&
          typeof (e as LibraryApp).name === "string",
      )
      .filter((e) => !isRetiredStoreTile(e));
  } catch {
    return [];
  }
}

function saveLibraryLocal(apps: LibraryApp[], key: string = LIBRARY_KEY): void {
  try {
    localStorage.setItem(key, JSON.stringify(apps.slice(0, MAX_APPS)));
  } catch {
    /* storage unavailable (private mode) — the library is a convenience */
  }
}

/**
 * Fired on `window` whenever the persisted library changes, so already-mounted
 * grids (Dashboard, /run launcher) can re-read it.
 */
export const LIBRARY_CHANGED_EVENT = "ato:library-changed";

function emitLibraryChanged(): void {
  try {
    window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  } catch {
    /* non-browser environment */
  }
}

// Sources the user removed, scoped per account (`accountTombstoneKey`): a
// source removed from the SERVER library must not resurface just because a
// stale local copy from before sign-in is still sitting in this account's
// local-fallback key (mirrors ato-api#276's own close condition 3, enforced
// server-side for the import path — this is the client-side analogue for the
// overlay-merge path).
//
// The `ato_seed_removed` prefix is FROZEN: per-account tombstones written by
// earlier versions must keep resolving.
const ACCOUNT_TOMBSTONES_KEY_PREFIX = "ato_seed_removed:acct:";

function loadTombstones(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === "string")
      : [];
  } catch {
    return [];
  }
}

function addTombstone(key: string, source: string): void {
  try {
    const tombstones = loadTombstones(key);
    if (!tombstones.includes(source)) {
      localStorage.setItem(key, JSON.stringify([...tombstones, source]));
    }
  } catch {
    /* storage unavailable */
  }
}

function removeTombstone(key: string, source: string): void {
  try {
    const tombstones = loadTombstones(key);
    if (!tombstones.includes(source)) return;
    localStorage.setItem(
      key,
      JSON.stringify(tombstones.filter((s) => s !== source)),
    );
  } catch {
    /* storage unavailable */
  }
}

/** Per-account key derivation for the signed-in local-fallback store
 *  (non-server-syncable sources) and its tombstones — NEVER the same
 *  physical key as the shared/unscoped `LIBRARY_KEY` used by anon mode, so two
 *  different accounts signed into the same browser never read or write each
 *  other's entries (round-2 finding, reviewer-S / reviewer-fresh-r1). */
function accountLocalKey(userId: string): string {
  return `${LIBRARY_KEY}:acct:${userId}`;
}

function accountTombstoneKey(userId: string): string {
  return `${ACCOUNT_TOMBSTONES_KEY_PREFIX}${userId}`;
}

function loadAccountTombstones(userId: string): string[] {
  return loadTombstones(accountTombstoneKey(userId));
}

function addAccountTombstone(userId: string, source: string): void {
  addTombstone(accountTombstoneKey(userId), source);
}

function removeAccountTombstone(userId: string, source: string): void {
  removeTombstone(accountTombstoneKey(userId), source);
}

/** Only ever called from a branch already gated by `isAuthed()`. */
function currentAccountId(): string {
  return authedUserId as string;
}

function removeLibraryAppLocal(source: string): LibraryApp[] {
  const apps = loadLibraryLocal();
  const next = apps.filter((a) => a.source !== source);
  if (next.length !== apps.length) {
    saveLibraryLocal(next);
    emitLibraryChanged();
  }
  return next;
}

function normalizeBinding(
  raw: unknown,
  expectedSource: string,
): SavedLaunchBinding | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as SavedLaunchBinding;
  // Unknown schema versions are dropped, not half-read.
  if (b.schemaVersion !== 1) return null;
  // The binding must belong to the entry it sits on — stale/corrupted
  // storage never half-applies to a different capsule.
  if (typeof b.sourceKey !== "string" || b.sourceKey !== expectedSource)
    return null;
  const kind = b.placement?.kind;
  if (kind !== "managed-cloud" && kind !== "external-runner") return null;
  if (kind === "external-runner" && typeof b.placement.runnerId !== "string")
    return null;
  if (typeof b.recordedAt !== "string") return null;
  return b;
}

const CLIENT_ID_KEY = "ato_client_id";

/** Stable per-browser id for install-location metadata reporting. */
export function getOrCreateClientId(): string {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return "unknown";
  }
}

/**
 * Best-effort install-location metadata report (PUT /v1/install-locations):
 * which client / runner / cloud placement last reached ready. Metadata only —
 * never contract content. Failures are swallowed (same posture as
 * enrichLibraryApp); the endpoint may not exist on older APIs.
 */
export async function reportInstallLocation(
  source: string,
  location: {
    kind: "pwa" | "connected-runner" | "managed-cloud" | "static-web";
    ref?: string | null;
  },
  fetchImpl: typeof fetch = (...args) => fetch(...args),
): Promise<void> {
  try {
    await fetchWithConnectionAuth(`${apiBase()}/v1/install-locations`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        capsule: source.trim(),
        location_kind: location.kind,
        location_ref: location.ref ?? "",
      }),
    }, fetchImpl);
  } catch {
    /* best-effort */
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Server-backed mode (ato-api#276) — session tracking, wire types, mappers,
// the request helper, and the mode-aware public API.
// ─────────────────────────────────────────────────────────────────────────

/** Same error-shape convention as `RunApiError` (runsClient.ts): a typed
 *  status+code so callers/tests can branch without string-matching a
 *  message. `status: 0` marks a network-layer failure (fetch itself threw)
 *  rather than a real HTTP response — see `maybeRunImport`'s use of it. */
export class LibraryApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "LibraryApiError";
  }
}

async function libraryRequest<T>(
  path: string,
  method: "GET" | "POST" | "DELETE",
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithConnectionAuth(`${apiBase()}${path}`, {
      method,
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new LibraryApiError(
      0,
      "network_error",
      err instanceof Error ? err.message : String(err),
    );
  }
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* some responses may have no body; tolerate it, `!res.ok` still throws */
  }
  if (!res.ok) {
    const j = (json ?? {}) as Record<string, string>;
    throw new LibraryApiError(
      res.status,
      j.error ?? (res.status === 401 ? "auth_required" : "library_api_error"),
      j.message ?? `HTTP ${res.status}`,
    );
  }
  return json as T;
}

// ── wire shapes (GET/POST /v1/account/library*) — see
// ato-api's routes/user_library.ts + services/user_library.ts, read directly
// as this lane's reference (never imported cross-repo). ──

interface ServerSavedLaunchHint {
  schema_version: 1;
  source_key: string;
  placement: {
    kind: "managed-cloud" | "external-runner";
    runner_id?: string | null;
    runner_compatibility_class?: string | null;
  };
  capsule_scoped_id?: string | null;
  consent_ref?: string | null;
  recorded_at: string;
}

interface ServerLibraryEntry {
  capsule_id: string;
  source: string;
  name: string;
  icon_url: string | null;
  pinned: boolean;
  last_launched_at: string | null;
  saved_launch_hint: ServerSavedLaunchHint | null;
  created_at: string;
  updated_at: string;
}

/**
 * Map a client SavedLaunchBinding to the server's `saved_launch_hint` wire
 * shape. `consent_ref` is DELIBERATELY NEVER SENT: the server pins it to a
 * 26-char ULID (`savedLaunchHintSchema`, services/user_library.ts) because
 * it is meant to reference a row id, but this client's `consentRef` is the
 * E302 approval reference, which is `blake3:<hex>`-shaped — a different,
 * much longer format the server's `.strict()` schema would reject outright,
 * 400ing the ENTIRE upsert (not just dropping the field). Omitting it here
 * is the only safe mapping; the server-side value (if ever populated by some
 * other caller) is likewise never trusted back — see `fromServerHint`.
 */
function toServerHint(binding: SavedLaunchBinding): Record<string, unknown> {
  return {
    schema_version: 1,
    source_key: binding.sourceKey,
    placement: {
      kind: binding.placement.kind,
      runner_id: binding.placement.runnerId ?? null,
      runner_compatibility_class:
        binding.placement.runnerCompatibilityClass ?? null,
    },
    // ato-api's `capsule_scoped_id` (when present) is restricted to
    // `publisher/slug` shape by the SAME `scopedCapsuleIdSchema` as
    // `source_key` (services/user_library.ts) — never send a value that
    // doesn't match it. A caller's own `capsuleScopedId` can legitimately be
    // a bare id in some fallback paths (e.g. GetRun.tsx's
    // `launch.capsule_ref ?? source`), which would 400 the whole upsert
    // exactly like an invalid `source_key` (round-3 finding, reviewer-T /
    // reviewer-fresh-r2) — guard here so every caller gets this for free.
    capsule_scoped_id:
      binding.capsuleScopedId && isStoreRef(binding.capsuleScopedId)
        ? binding.capsuleScopedId
        : null,
    recorded_at: binding.recordedAt,
  };
}

/**
 * A server-safe `source_key` for `saved_launch_hint`, or null when none
 * exists. ato-api's `savedLaunchHintSchema.source_key` (services/user_library
 * .ts's `scopedCapsuleIdSchema`) is ALWAYS `publisher/slug`-shaped — it has
 * no ULID exception the way `POST /library`'s plain `source` field does
 * (`resolveLibrarySource` accepts either) — so sending a bare ULID there
 * 400s the ENTIRE upsert, not just the hint (round-3 finding, reviewer-T /
 * reviewer-fresh-r2). Prefer the source itself when it is already
 * scoped-shaped; fall back to the binding's own `capsuleScopedId` ONLY when
 * that is ALSO scoped-shaped (never trust it blindly — see `toServerHint`'s
 * doc comment); otherwise there is no safe wire encoding at all.
 */
function hintSourceKeyFor(
  binding: SavedLaunchBinding,
  source: string,
): string | null {
  if (isStoreRef(source)) return source;
  if (binding.capsuleScopedId && isStoreRef(binding.capsuleScopedId)) {
    return binding.capsuleScopedId;
  }
  return null;
}

function fromServerHint(
  hint: ServerSavedLaunchHint | null,
  expectedSource: string,
): SavedLaunchBinding | null {
  if (!hint) return null;
  return normalizeBinding(
    {
      schemaVersion: 1,
      sourceKey: hint.source_key,
      placement: {
        kind: hint.placement.kind,
        runnerId: hint.placement.runner_id ?? null,
        runnerCompatibilityClass:
          hint.placement.runner_compatibility_class ?? null,
      },
      capsuleScopedId: hint.capsule_scoped_id ?? null,
      // Never round-tripped — see toServerHint's doc comment. Any value
      // stored server-side is a ULID, not this client's blake3 ref shape,
      // so it could never be a valid consentRef for this client anyway.
      consentRef: null,
      recordedAt: hint.recorded_at,
    },
    expectedSource,
  );
}

function fromServerLibraryEntry(entry: ServerLibraryEntry): LibraryApp {
  return {
    source: entry.source,
    name: entry.name,
    iconUrl: entry.icon_url,
    addedAt: entry.created_at,
    lastRunAt: entry.last_launched_at ?? entry.created_at,
    // The server's `pinned` field is a not-yet-built pin/unpin feature with no
    // UI in this lane (see routes/user_library.ts's /pin, /unpin), so it is
    // deliberately not surfaced here.
    binding: fromServerHint(entry.saved_launch_hint, entry.source),
  };
}

// ── session tracking ──

let authedUserId: string | null = null;
/** null = not yet loaded (or load in flight/failed) for the current
 *  session; loadLibrary() then falls back to the local overlay ONLY (never
 *  local library data as if it were the server's — see the module doc
 *  comment) until a load succeeds. */
let serverEntries: LibraryApp[] | null = null;
/** Bumped on every session change so a slow in-flight GET from a PREVIOUS
 *  user/session can never land after a sign-out or a different sign-in. */
let serverLoadToken = 0;
/** Sources explicitly removed (optimistically, via `removeServerLibraryApp`)
 *  since the current session started. Consulted by `loadServerLibrary`'s
 *  merge so a REMOVE that raced ahead of a slower in-flight GET — whose
 *  response may still list the source because the DELETE simply hadn't been
 *  processed server-side yet — is never blindly resurrected. Mirror image of
 *  the round-1 "merge, don't clobber" fix, which protected an optimistic ADD
 *  from the same race (round-2 finding, reviewer-T finding 2). Reset on every
 *  `setLibrarySession` transition, and cleared per-source by
 *  `touchServerEntryInMemory` (an explicit re-add always wins over a pending
 *  removal marker). */
let removedSinceLoad = new Set<string>();
/** Maps a server-CONFIRMED `source` (always `publisher/slug` —
 *  `listLibraryEntries` never echoes a raw id, see `fromServerLibraryEntry`)
 *  to its underlying `capsules.id`, populated from every successful GET.
 *  This is what lets a bare-ULID-keyed optimistic entry (added via
 *  `touchServerEntryInMemory` before the server's canonical form was known
 *  — a bare ULID IS the capsule's raw id, see `isUlidLike`'s doc comment) be
 *  recognized as the SAME row once a GET confirms it, instead of rendering
 *  as a second, permanently misnamed tile for the rest of the session
 *  (round-3 finding, reviewer-U) — see `dedupeByCapsuleId`. Reset on every
 *  session transition. */
let capsuleIdBySource = new Map<string, string>();

function isAuthed(): boolean {
  return authedUserId !== null;
}

/**
 * Ops-facing OPT-IN gate (round-4 finding, reviewer-T / reviewer-fresh-r3;
 * round-5 finding B — deploy-ordering risk): the server routes this module
 * calls exist only on ato-api's `auth/my-apps-276` branch (PR #280), still
 * OPEN/DRAFT as of this writing and NOT deployed to any environment — so
 * `/v1/account/library*` currently 404s everywhere. Deploying this ato-pwa
 * lane is only safe paired with (never ahead of) that PR landing.
 *
 * This is therefore an OPT-IN flag, DISABLED by default: server-backed My
 * Apps stays off unless `VITE_MY_APPS_SERVER_LIBRARY_ENABLED=true` is set
 * explicitly. A prior revision defaulted this ON (an opt-OUT
 * `VITE_MY_APPS_SERVER_LIBRARY_DISABLED`), which meant shipping this lane
 * as-is would optimistically appear to save every signed-in user's
 * save/touch/pin — then silently revert on reload as the 404s were swallowed
 * by the fire-and-forget catch handlers. Flipping the polarity makes the
 * SAFE state the default: a human sets `VITE_MY_APPS_SERVER_LIBRARY_ENABLED`
 * once — and only once — ato-api#276 (PR #280) is confirmed deployed to the
 * target environment. Until then every signed-in session behaves exactly
 * like pure local-storage (anon-mode-shaped) mode — no `/v1/account/library*`
 * call is ever attempted, so there is no 404 to swallow and nothing to
 * silently revert on reload.
 */
function serverLibraryEnabled(): boolean {
  return (
    (import.meta.env.VITE_MY_APPS_SERVER_LIBRARY_ENABLED as
      string | undefined) === "true"
  );
}

/**
 * Tell this module the current account session — call once from wherever
 * the app already tracks sign-in state (App.tsx's `accountState`, sourced
 * from `fetchAccountSession()` / `AccountAuthState`). This is a pure
 * notification sink: it never fetches or derives session state itself, so
 * it does not duplicate or replace the existing auth mechanism.
 *
 * On the FIRST transition into a signed-in user id (fresh sign-in, or an
 * already-signed-in session discovered on app load), this kicks off the
 * initial server library fetch (`loadServerLibrary`) — a pure READ. It does
 * NOT run the localStorage merge-import: that is an explicit, opt-in action
 * (`importLocalLibraryToAccount`), never a side effect of signing in, because
 * "Save to My Apps" and "Continue this session" must stay separate (RFC
 * ato-api#261 §5; round-5 findings C + D — see the module doc comment). A
 * transition to `null` (sign-out) reverts reads/writes to local mode, exactly
 * as before this file grew a server mode; because the import never ran on
 * sign-in, the shared/unscoped anonymous library is still intact for the
 * anonymous view after signing back out.
 */
export function setLibrarySession(session: AccountSession | null): void {
  const nextUserId = serverLibraryEnabled() ? (session?.user.id ?? null) : null;
  if (nextUserId === authedUserId) return;
  authedUserId = nextUserId;
  serverLoadToken += 1;
  const token = serverLoadToken;
  removedSinceLoad = new Set(); // fresh session — no pending removals to protect yet
  capsuleIdBySource = new Map();
  if (!nextUserId) {
    serverEntries = null;
    emitLibraryChanged();
    return;
  }
  serverEntries = [];
  // Signed-in mode starts composing its (initially empty) server-backed view
  // immediately — an already-mounted grid (DashboardApps) must re-read NOW
  // rather than keep showing whatever anon-mode local entries were on
  // screen before sign-in, with no signal to refresh if the confirming GET
  // below never resolves (round-3 finding, reviewer-U: this transition
  // previously only emitted on SIGN-OUT, never on sign-in).
  emitLibraryChanged();
  void loadServerLibrary(nextUserId, token);
  // NOTE: no `maybeRunImport` here — the merge-import is explicit-only
  // (`importLocalLibraryToAccount`), never triggered by a bare sign-in
  // (round-5 findings C + D). See the module doc comment.
}

/**
 * EXPLICIT, opt-in "Save this browser's local (anonymous) My Apps into my
 * signed-in account" action — the one-time localStorage merge-import
 * (ato-api#276). This is the ONLY entry point that folds anonymous history
 * into the account's durable, cross-device library.
 *
 * DELIBERATELY NOT called from `setLibrarySession` or any bare sign-in /
 * OAuth-callback path. Per RFC ato-api#261 §5, "Save to My Apps" and
 * "Continue this session" are always separate, explicit operations: signing
 * in merely to continue a live preview session must never sweep this
 * browser's anonymous usage history into the account (round-5 finding D), and
 * must never strand the anonymous view's entries with no way back when an
 * import never confirms (round-5 finding C). The claim + POST inside
 * `maybeRunImport` are exactly that sweep, so they only run on this explicit
 * call.
 *
 * INTERIM: no UI wires this yet — the explicit "Save to My Apps" intent flow
 * is ato-api#267's later lane (RFC §4's short-lived post-auth intent). Until
 * then this is unreferenced by product code, so in production the import
 * never runs and an ordinary sign-in is a no-op for the anonymous library.
 * The full correct fix (a real "Save my apps" affordance that arms this on a
 * genuine save intent) belongs with that lane; this export is the seam it
 * will call, and is what the finding C/D/A tests drive directly.
 */
export async function importLocalLibraryToAccount(): Promise<void> {
  if (!serverLibraryEnabled()) return;
  const userId = authedUserId;
  if (!userId) return;
  await maybeRunImport(userId);
}

async function fetchServerLibraryEntries(): Promise<ServerLibraryEntry[]> {
  const data = await libraryRequest<{ entries?: ServerLibraryEntry[] }>(
    "/v1/account/library",
    "GET",
  );
  return Array.isArray(data?.entries) ? data.entries : [];
}

async function loadServerLibrary(userId: string, token: number): Promise<void> {
  try {
    const rawEntries = await fetchServerLibraryEntries();
    if (token !== serverLoadToken || authedUserId !== userId) return; // superseded
    const entries = rawEntries.map(fromServerLibraryEntry);
    // Refresh capsule-identity knowledge from every confirmed source (see
    // capsuleIdBySource's doc comment) — never reset here, only ever added
    // to, so a ULID touched earlier in THIS session can still dedupe
    // against a capsule this (possibly later) GET confirms.
    for (const e of rawEntries) capsuleIdBySource.set(e.source, e.capsule_id);
    // Merge, don't clobber (round-1 finding, reviewer-T): an optimistic
    // touch/save made after this GET started but before it resolved already
    // appended to `serverEntries` in memory (`touchServerEntryInMemory`) — a
    // blind overwrite here would silently drop it the instant this (slower)
    // GET lands. `serverEntries` is always reset to `[]` by
    // `setLibrarySession` immediately before this fetch starts, so any
    // source present in it now that this fetch's OWN result doesn't know
    // about can only be such an in-flight race, never stale pre-existing
    // data — every source the fetch DOES know about stays authoritative
    // ("server wins on a source collision", per the module doc comment).
    const fetchedSources = new Set(entries.map((e) => e.source));
    const racedOptimisticEntries = (serverEntries ?? []).filter(
      (a) => !fetchedSources.has(a.source),
    );
    // Drop any source explicitly removed (optimistically) since this load
    // started, even if THIS fetch's own result still lists it — that can
    // only mean the fetch is a stale response where the DELETE simply hadn't
    // been processed server-side yet when the GET was issued (round-2
    // finding, reviewer-T finding 2: an optimistic remove racing a slower
    // in-flight GET was previously resurrected the instant that GET landed,
    // the mirror image of the round-1 optimistic-ADD race above).
    const liveEntries =
      removedSinceLoad.size === 0
        ? entries
        : entries.filter((e) => !removedSinceLoad.has(e.source));
    serverEntries = [...racedOptimisticEntries, ...liveEntries];
    emitLibraryChanged();
  } catch (err) {
    if (token !== serverLoadToken || authedUserId !== userId) return;
    // Fail gracefully: leave serverEntries as the empty array already set in
    // setLibrarySession (never fall back to reading local anon data as if it
    // were this account's library — that would re-diverge local/server
    // state exactly as the merge-import's idempotency guarantee assumes it
    // cannot). A later remount / sign-in-again retries this fetch.
    if (err instanceof LibraryApiError && err.status === 404) {
      // A distinct, more actionable message: this specific status is the
      // expected symptom of ato-pwa's account/library-server-c2 lane being
      // deployed ahead of (or desynced from) ato-api's PR #280 landing (see
      // the module doc comment) — never a generic transient failure
      // (round-3 finding, reviewer-fresh-r2).
      console.warn(
        "[libraryClient] GET /v1/account/library returned 404 — ato-api's server library routes (PR #280) may not be deployed yet; falling back to the local-only overlay for this session",
        err,
      );
    } else {
      console.warn("[libraryClient] failed to load account library", err);
    }
    // Already-mounted grids must re-read NOW: they must not keep showing
    // stale pre-sign-in/anon-mode data as if it were this account's synced
    // library just because the confirming GET failed (round-3 finding,
    // reviewer-U) — loadLibrary() still correctly falls back to the
    // per-account local overlay only, never anon-mode data.
    emitLibraryChanged();
  }
}

// ── merge-import (one-time, per browser per user) ──

/** Matches ato-api's LIBRARY_IMPORT_SOURCE / LIBRARY_IMPORT_VERSION
 *  (services/user_library.ts) — no cross-repo import path exists, so this is
 *  the client-side copy of that literal contract. */
const SERVER_LIBRARY_IMPORT_SOURCE = "ato_app_library_v1";
const SERVER_LIBRARY_IMPORT_VERSION = 1;

const IMPORT_MARKER_PREFIX = "ato_library_import_attempted:";

function hasAttemptedImport(userId: string): boolean {
  try {
    return localStorage.getItem(`${IMPORT_MARKER_PREFIX}${userId}`) === "1";
  } catch {
    // Storage unavailable — treat as "not yet attempted". The server's own
    // per-(user,source) guard (library_import_state) makes a repeat POST an
    // idempotent no-op regardless, so this only risks a redundant call.
    return false;
  }
}

function markImportAttempted(userId: string): void {
  try {
    localStorage.setItem(`${IMPORT_MARKER_PREFIX}${userId}`, "1");
  } catch {
    /* best-effort marker only */
  }
}

/**
 * Claim (move) EVERY non-Store-tile entry currently sitting in the
 * shared/unscoped local library into THIS account's own local-fallback key
 * (`accountLocalKey`) — unconditionally, synchronously, and as the very
 * FIRST action `maybeRunImport` takes: before it even checks
 * `hasAttemptedImport`, and long before any `await`.
 *
 * This used to only happen for entries the server library can never resolve
 * by SHAPE (a GitHub URL, `capsule://` ref, ...); a server-RESOLVABLE entry
 * was left sitting in the shared key until the import POST resolved with a
 * confirmed 2xx, at which point a since-removed helper swept it out. That
 * left a window — the entire time between sign-in and the POST settling, or
 * indefinitely on any non-2xx outcome — during which the entry was still
 * readable from the shared key. Two round-4 findings independently landed
 * in that window:
 *   - reviewer-S: a FAILED import (network error, 4xx, 5xx — or, before
 *     ato-api PR #280 lands, a guaranteed 404) left the entry in the shared
 *     key indefinitely, so a totally unrelated account signing into the
 *     same browser afterward read it as its OWN pre-signin data and merged
 *     it into ITS server library.
 *   - reviewer-fresh-r3: even on the success path, a fast account switch
 *     during the in-flight window (before the first account's POST
 *     resolved) let a SECOND account's own `maybeRunImport` read and submit
 *     the first account's still-unclaimed entries under its own session.
 *
 * Moving the claim itself here — before any network call, entirely
 * synchronous — closes both: by the time `maybeRunImport(userId)` returns
 * control to the event loop (i.e. reaches its first `await`), the shared
 * key no longer holds anything for a subsequent sign-in (by this account or
 * any other) to see, regardless of what the import call ends up doing.
 * Whether a resolvable entry is EVER actually deleted from this account's
 * own key now depends only on a CONFIRMED server merge — see
 * `maybeRunImport`.
 *
 * Idempotent: entries already present at the destination are never
 * duplicated, and re-running after the shared key is already empty is a
 * no-op.
 */
function claimSharedLocalEntriesForAccount(userId: string): void {
  const claimable = loadLibraryLocal(LIBRARY_KEY);
  if (claimable.length === 0) return;

  const acctKey = accountLocalKey(userId);
  const acctApps = loadLibraryLocal(acctKey);
  const merged = [...acctApps];
  for (const entry of claimable) {
    if (merged.some((a) => a.source === entry.source)) continue;
    merged.push(entry);
  }
  saveLibraryLocal(merged, acctKey);
  saveLibraryLocal([], LIBRARY_KEY);
  emitLibraryChanged();
}

/**
 * Remove exactly the given sources from this account's own local-fallback
 * key — called only once the server has CONFIRMED the whole import batch
 * merged (see `maybeRunImport`). Never touches the shared/unscoped key: by
 * the time this runs, `claimSharedLocalEntriesForAccount` has already moved
 * everything out of it.
 */
function removeAccountLocalEntries(
  userId: string,
  sources: readonly string[],
): void {
  if (sources.length === 0) return;
  const acctKey = accountLocalKey(userId);
  const sourceSet = new Set(sources);
  const apps = loadLibraryLocal(acctKey);
  const remaining = apps.filter((a) => !sourceSet.has(a.source));
  if (remaining.length !== apps.length) saveLibraryLocal(remaining, acctKey);
}

/**
 * Reconcile this account's own local-fallback key against the server's
 * CURRENT authoritative source list (round-5 finding A): drop any
 * SERVER-RESOLVABLE entry the server no longer lists, so a source removed
 * server-side cannot resurrect as a ghost tile through the local overlay
 * (`mergeBySource`'s `extraLocal` branch). Shape-unresolvable sources (a
 * GitHub URL, `capsule://` ref) are ALWAYS kept — the server can never list
 * them, so their absence is expected, and they legitimately live only in this
 * key. Emits a change so a mounted grid drops the ghost immediately.
 */
function reconcileAccountLocalAgainstServer(
  userId: string,
  serverSources: ReadonlySet<string>,
): void {
  const acctKey = accountLocalKey(userId);
  const apps = loadLibraryLocal(acctKey);
  const remaining = apps.filter(
    (a) => !isServerResolvable(a.source) || serverSources.has(a.source),
  );
  if (remaining.length !== apps.length) {
    saveLibraryLocal(remaining, acctKey);
    emitLibraryChanged();
  }
}

/**
 * One-time merge-import of this browser's localStorage library into the
 * signed-in user's server library (ato-api#276). The client-side marker
 * (`hasAttemptedImport`/`markImportAttempted`) is purely a same-page-load
 * optimization to skip the network call — the REAL idempotency guarantee is
 * server-side (`library_import_state`, keyed on user+source), so a repeat
 * call (new device, marker cleared, race) is always a safe no-op.
 *
 * Every non-Store entry is claimed out of the shared key up front,
 * unconditionally (`claimSharedLocalEntriesForAccount`) — see that
 * function's doc comment for why. From here on this function only ever
 * reads/writes THIS account's own local-fallback key; the shared key is out
 * of the picture.
 *
 * The marker is set on any genuine 2xx response — including `imported:
 * false` (another device already consumed this account's one-shot
 * server-side guard: retrying can never succeed for this account, so
 * there's no reason to keep trying) — but NEVER on a network-level throw
 * (`status === 0`) or any other HTTP error status (4xx incl. an unexpected
 * 404 while ato-api PR #280 isn't deployed yet, or 5xx), because none of
 * those confirm the request was even processed (round-3 finding:
 * reviewer-S finding 2 / reviewer-T / reviewer-fresh-r2 finding 3).
 *
 * Outcomes for the claimed local entries:
 *   - `imported === true && skipped_count === 0` — the ENTIRE sent batch
 *     merged; delete the claimed copies from this account's own key.
 *   - `imported === false` — the account's one-shot server-side guard was
 *     already consumed (typically another device). RECONCILE the local
 *     fallback against the current authoritative GET and drop any
 *     server-resolvable entry the server no longer lists, so an app removed
 *     server-side does not resurrect as a ghost tile (round-5 finding A;
 *     ato-api#276 close condition 3). Before this, `imported: false` left
 *     the claimed copy in place forever, and `mergeBySource`'s `extraLocal`
 *     branch kept re-surfacing it as a clickable tile even though the GET
 *     did not contain it — a resurrection the user had to undo by hand
 *     (reviewer findings 1 / 5 / 6). Reconciling accepts a small risk: a
 *     genuinely never-synced second-device entry could be dropped — the same
 *     risk class already accepted elsewhere; a ghost tile is worse than
 *     trusting the server's own list.
 *   - a partial `skipped_count > 0`, or an error (network throw, 4xx incl.
 *     an unexpected 404 while PR #280 isn't deployed, 5xx) — leave the
 *     claimed copies in place: the aggregate response cannot say WHICH
 *     sources (if any) landed, and an error confirms nothing was processed
 *     at all. Blindly clearing on a partial 200 previously destroyed a
 *     second device's own never-merged local entries (round-4 finding,
 *     reviewer-T / reviewer-U). The per-account fallback key carries no
 *     cross-account risk (already private to this account), and a source the
 *     server DOES later confirm dedupes away via `mergeBySource` on the next
 *     GET.
 */
async function maybeRunImport(userId: string): Promise<void> {
  // Claim first, unconditionally, synchronously — before the
  // hasAttemptedImport short-circuit and before any await. This whole
  // function only runs behind the explicit `importLocalLibraryToAccount`
  // action (round-5 findings C + D), so the sweep it performs is the user's
  // deliberate "Save to My Apps", never a side effect of signing in. See
  // claimSharedLocalEntriesForAccount's doc comment (round-4 findings,
  // reviewer-S / reviewer-fresh-r3).
  claimSharedLocalEntriesForAccount(userId);

  if (hasAttemptedImport(userId)) return;

  const acctKey = accountLocalKey(userId);
  const localEntries = loadLibraryLocal(acctKey).filter((a) =>
    isServerResolvable(a.source),
  );
  if (localEntries.length === 0) {
    markImportAttempted(userId); // nothing left to import; avoid retrying forever
    return;
  }
  const payload = {
    source: SERVER_LIBRARY_IMPORT_SOURCE,
    version: SERVER_LIBRARY_IMPORT_VERSION,
    entries: localEntries.map((a) => {
      const hintSourceKey = a.binding
        ? hintSourceKeyFor(a.binding, a.source)
        : null;
      return {
        source: a.source,
        ...(a.lastRunAt ? { last_launched_at: a.lastRunAt } : {}),
        ...(a.binding && hintSourceKey
          ? {
              saved_launch_hint: toServerHint({
                ...a.binding,
                sourceKey: hintSourceKey,
              }),
            }
          : {}),
      };
    }),
  };
  try {
    const result = await libraryRequest<{
      imported?: boolean;
      merged_count?: number;
      skipped_count?: number;
    }>("/v1/account/library/import", "POST", payload);
    markImportAttempted(userId);
    if (result?.imported === true) {
      // Only delete what we're SURE the server actually merged — see the
      // function's doc comment.
      if ((result.skipped_count ?? 0) === 0) {
        removeAccountLocalEntries(
          userId,
          localEntries.map((a) => a.source),
        );
      }
      // Some or all of the sent entries may have merged — refresh once so
      // the grid reflects it (and so a merged entry's local-overlay copy,
      // if left in place above for the partial case, dedupes away via
      // mergeBySource the moment serverEntries reflects it), provided this
      // is still the current session.
      if (authedUserId === userId)
        void loadServerLibrary(userId, serverLoadToken);
    } else if (result?.imported === false) {
      // `imported: false` = this account's one-shot server-side import guard
      // was already consumed (typically by another device). The aggregate
      // response carries no per-source status, so this client cannot tell
      // "already merged elsewhere" from "removed server-side" from "never
      // sent by any device" — but it CAN ask the server what the account's
      // library authoritatively contains right now. Reconcile the local
      // fallback against that list and drop any SERVER-RESOLVABLE entry the
      // server no longer lists, so an app removed server-side does not
      // resurrect as a permanent ghost tile via the local overlay (round-5
      // finding A; ato-api#276 close condition 3). This accepts a small risk
      // — a genuinely never-synced second-device entry could be dropped —
      // which is the same risk class already accepted elsewhere: a ghost
      // tile the user must remove by hand is worse than a UI that trusts the
      // server's own authoritative list. Shape-unresolvable entries (GitHub
      // URL, `capsule://`) are never touched — the server can never list
      // them, so their absence is expected, not a removal.
      try {
        const current = await fetchServerLibraryEntries();
        if (authedUserId === userId) {
          reconcileAccountLocalAgainstServer(
            userId,
            new Set(current.map((e) => e.source)),
          );
          for (const e of current)
            capsuleIdBySource.set(e.source, e.capsule_id);
          void loadServerLibrary(userId, serverLoadToken);
        }
      } catch (reconcileErr) {
        // No authoritative list to trust — leave the local fallback intact
        // (conservative, same posture as the error path below).
        console.warn(
          "[libraryClient] reconcile after imported:false failed; leaving the local fallback intact",
          reconcileErr,
        );
      }
    }
  } catch (err) {
    // Any non-2xx outcome (network throw, 4xx, or 5xx) means we have NO
    // confirmation anything merged server-side — never mark attempted or
    // remove the claimed local copy here. See the function's doc comment.
    if (err instanceof LibraryApiError && err.status === 404) {
      console.warn(
        "[libraryClient] POST /v1/account/library/import returned 404 — ato-api's server library routes (PR #280) may not be deployed yet; will retry later",
        err,
      );
    } else {
      console.warn("[libraryClient] library merge-import failed", err);
    }
  }
}

// ── local overlay + merge (signed-in loadLibrary()) ──

/**
 * The signed-in local overlay: this account's own local-fallback entries
 * (non-server-syncable sources — see `touchServerLibraryApp`), read from a
 * key scoped by `userId` so a browser shared by a different account never
 * surfaces here (round-2 finding, reviewer-S / reviewer-fresh-r1). This is
 * also why a STALE store-ref entry left over in the shared/unscoped key from
 * before sign-in can never resurrect through this overlay (round-2 finding,
 * reviewer-T finding 3): nothing is read from that shared key, and ordinary
 * entries come exclusively from the per-account key, which nothing but the
 * local-fallback writers below ever populate.
 */
function localOverlay(): LibraryApp[] {
  const userId = currentAccountId(); // only ever called from loadLibrary()'s authed branch
  const tombstones = new Set(loadAccountTombstones(userId));
  return loadLibraryLocal(accountLocalKey(userId)).filter(
    (a) => !tombstones.has(a.source),
  );
}

/** Server entries first (already pinned-then-recency ordered by the API),
 *  and any local-only overlay entry not yet represented server-side appended
 *  at the end. */
function mergeBySource(
  server: LibraryApp[],
  overlay: LibraryApp[],
): LibraryApp[] {
  const serverSources = new Set(server.map((a) => a.source));
  return [...server, ...overlay.filter((a) => !serverSources.has(a.source))];
}

/**
 * Collapse duplicate tiles for the SAME underlying capsule reached via two
 * different ref forms within one tab session — e.g. a `publisher/slug` Store
 * save and a bare-ULID `/open/<id>` touch/binding for the same app (round-3
 * finding, reviewer-U). Before this, `touchServerEntryInMemory` keyed each
 * optimistic entry by whatever raw source string the caller passed, and
 * nothing ever reconciled a ULID-keyed entry against the canonical
 * `publisher/slug` entry the server always echoes back for the SAME capsule
 * (`listLibraryEntries` never returns a raw id as `source`) — producing a
 * second, permanently-misnamed (raw-ULID, no icon) tile for the rest of the
 * session.
 *
 * A bare-ULID source IS the capsule's raw id (`capsule_ref.ts`'s
 * `isUlidLike` / `kind: "id"`, read directly), so its identity is always
 * known without a lookup; a `publisher/slug` source's identity is known once
 * `capsuleIdBySource` has seen it via a GET. Entries whose identity isn't
 * yet known (never confirmed by any GET) are left alone — nothing to dedupe
 * against yet, and they may be genuinely distinct capsules. When two entries
 * DO share an identity, the non-ULID one always wins (it carries the real
 * name/icon from the server join; a ULID stub never does), independent of
 * list order.
 */
function dedupeByCapsuleId(list: LibraryApp[]): LibraryApp[] {
  const capsuleIdOf = (a: LibraryApp): string | null =>
    isUlidLike(a.source) ? a.source : (capsuleIdBySource.get(a.source) ?? null);

  const winners = new Map<string, LibraryApp>();
  for (const a of list) {
    const capsuleId = capsuleIdOf(a);
    if (!capsuleId) continue;
    const current = winners.get(capsuleId);
    if (!current || (isUlidLike(current.source) && !isUlidLike(a.source))) {
      winners.set(capsuleId, a);
    }
  }
  return list.filter((a) => {
    const capsuleId = capsuleIdOf(a);
    return !capsuleId || winners.get(capsuleId) === a;
  });
}

// ── mode-aware public API ──

export function loadLibrary(): LibraryApp[] {
  if (isAuthed()) {
    return dedupeByCapsuleId(
      mergeBySource(serverEntries ?? [], localOverlay()),
    );
  }
  return loadLibraryLocal();
}

/**
 * Record a run of `source`: upsert the entry and bump `lastRunAt` so the
 * launcher grid stays ordered by recency. Returns the updated library.
 */
export function touchLibraryApp(source: string): LibraryApp[] {
  const trimmed = source.trim();
  if (!trimmed) return loadLibrary();
  if (isAuthed()) return touchServerLibraryApp(trimmed);
  return touchLibraryAppLocal(trimmed);
}

function touchLibraryAppLocal(
  trimmed: string,
  key: string = LIBRARY_KEY,
): LibraryApp[] {
  const now = new Date().toISOString();
  const apps = loadLibraryLocal(key);
  const existing = apps.find((a) => a.source === trimmed);
  let next: LibraryApp[];
  if (existing) {
    next = [
      { ...existing, lastRunAt: now },
      ...apps.filter((a) => a.source !== trimmed),
    ];
  } else {
    next = [
      {
        source: trimmed,
        name: slugOf(trimmed),
        iconUrl: null,
        addedAt: now,
        lastRunAt: now,
      },
      ...apps,
    ];
  }
  saveLibraryLocal(next, key);
  return next;
}

/** Optimistic in-memory add/touch + a fire-and-forget POST — mirrors the
 *  synchronous return contract every caller already relies on (e.g.
 *  `setLibrary(touchLibraryApp(app.source))`). A failed POST is logged and
 *  left as-is: the entry stays in this tab's in-memory `serverEntries` for
 *  the rest of the session but is never written to localStorage as if it
 *  were confirmed server state, so a reload always reflects the server's
 *  real, current library rather than a client-invented one. */
function touchServerEntryInMemory(source: string): LibraryApp {
  removedSinceLoad.delete(source); // an explicit re-add always wins over a pending removal marker
  const now = new Date().toISOString();
  const list = serverEntries ?? [];
  const existing = list.find((a) => a.source === source);
  const entry: LibraryApp = existing
    ? { ...existing, lastRunAt: now }
    : {
        source,
        name: slugOf(source),
        iconUrl: null,
        addedAt: now,
        lastRunAt: now,
      };
  serverEntries = [entry, ...list.filter((a) => a.source !== source)];
  return entry;
}

function touchServerLibraryApp(source: string): LibraryApp[] {
  if (!isServerResolvable(source)) {
    // The server library can only resolve a ULID or `publisher/slug` ref
    // (see the module doc comment) — a GitHub URL or `capsule://` ref would
    // 400 (`invalid_source`) on POST. Previously this still optimistically
    // added the entry to `serverEntries` and fired the doomed POST, so the
    // UI showed it as saved for the rest of the tab session, then it
    // silently vanished on the next reload/`loadServerLibrary()` (round-1
    // finding, reviewer-T). Fall back to a LOCAL-ONLY store instead: it
    // actually persists, and `loadLibrary()`'s `localOverlay()` merge still
    // surfaces it alongside the server list. Scoped to THIS account's own
    // key (round-2 finding, reviewer-S / reviewer-fresh-r1) — never the
    // shared/unscoped key anon mode and other accounts on this browser use.
    const userId = currentAccountId();
    removeAccountTombstone(userId, source); // an explicit touch is a deliberate re-add
    touchLibraryAppLocal(source, accountLocalKey(userId));
    return loadLibrary();
  }
  touchServerEntryInMemory(source);
  void libraryRequest("/v1/account/library", "POST", { source }).catch(
    (err) => {
      console.warn("[libraryClient] failed to save library entry", err);
    },
  );
  return loadLibrary();
}

export function removeLibraryApp(source: string): LibraryApp[] {
  if (isAuthed()) return removeServerLibraryApp(source);
  return removeLibraryAppLocal(source);
}

function removeServerLibraryApp(source: string): LibraryApp[] {
  const userId = currentAccountId();
  // Round-5 finding E (cross-account isolation): a signed-in "Remove" must
  // NEVER mutate the shared/unscoped store. That key is read by anon mode and
  // by every OTHER account on this browser (an anon "My Apps" list, ...), so
  // writing a shared tombstone here permanently suppressed that source for
  // everyone (and deleting from the shared library key stole an entry the
  // signed-in user cannot even see — `localOverlay` reads only this account's
  // own key, never ordinary entries from the shared one). A
  // prior revision touched both, mirroring the old auto-claim's residue
  // handling; with the merge-import now explicit-only (findings C + D) there
  // is no such residue to clean, and the mutation was pure leakage.
  //
  // Removal is therefore confined to THIS account's own scoped stores: the
  // in-memory server list, this account's local-fallback key, and this
  // account's own tombstone. (Round-1 finding, reviewer-U: act
  // unconditionally on all of them, not only when the source was cached in
  // `serverEntries`, so an overlay-only entry — local fallback, pre-GET
  // touch, degraded GET-failure state — is actually removable.)
  serverEntries = (serverEntries ?? []).filter((a) => a.source !== source);
  removedSinceLoad.add(source); // protects against a stale in-flight GET — see loadServerLibrary

  const acctKey = accountLocalKey(userId);
  const acctApps = loadLibraryLocal(acctKey);
  const nextAcct = acctApps.filter((a) => a.source !== source);
  if (nextAcct.length !== acctApps.length) saveLibraryLocal(nextAcct, acctKey);

  // Per-account tombstone only (never the shared one — finding E): it closes
  // the loop with `localOverlay()`, which would otherwise resurface a stale
  // copy of the same source from this account's own local-fallback key (see
  // the ACCOUNT_TOMBSTONES_KEY_PREFIX / accountTombstoneKey doc comments).
  addAccountTombstone(userId, source);
  // Fire the DELETE unconditionally too — ato-api's DELETE is documented
  // idempotent (routes/user_library.ts: "removing an absent entry is still
  // 200"), so this is always safe, including when nothing was found in
  // EITHER local store above (the source may still exist server-side, e.g.
  // if the initial GET simply hasn't resolved yet).
  void libraryRequest(
    `/v1/account/library?source=${encodeURIComponent(source)}`,
    "DELETE",
  ).catch((err) => {
    console.warn("[libraryClient] failed to remove library entry", err);
  });
  emitLibraryChanged();
  return loadLibrary();
}

/**
 * Fetch display name + icon for a store ref and persist them onto the
 * library entry. Best-effort: network/auth failures leave the entry as-is.
 * Returns the updated library, or null when nothing changed.
 *
 * No-op when signed in: `GET /v1/account/library` already joins the
 * capsule's name/icon server-side (services/user_library.ts's
 * `listLibraryEntries`), so there is nothing left to enrich.
 */
export async function enrichLibraryApp(
  source: string,
  // Lexical wrapper: a bare `fetch` reference can throw "Illegal invocation"
  // when invoked detached from window in some browsers.
  fetchImpl: typeof fetch = (...args) => fetch(...args),
): Promise<LibraryApp[] | null> {
  if (isAuthed()) return null;
  return enrichLibraryAppLocal(source, fetchImpl);
}

interface CapsuleDetailLike {
  name?: unknown;
  icon?: unknown;
  store_icon?: unknown;
}

async function enrichLibraryAppLocal(
  source: string,
  fetchImpl: typeof fetch,
): Promise<LibraryApp[] | null> {
  const trimmed = source.trim();
  if (!isStoreRef(trimmed)) return null;
  const existing = loadLibraryLocal().find((a) => a.source === trimmed);
  // Already enriched (icon present) — skip the refetch; entries are small
  // and stale icons resolve themselves on the next add.
  if (existing?.iconUrl) return null;
  try {
    const res = await fetchImpl(`${apiBase()}/v1/capsules/by/${trimmed}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const detail = (await res.json()) as CapsuleDetailLike;
    const name =
      typeof detail.name === "string" && detail.name ? detail.name : null;
    const iconUrl =
      typeof detail.icon === "string" && detail.icon
        ? detail.icon
        : typeof detail.store_icon === "string" && detail.store_icon
          ? detail.store_icon
          : null;
    if (!name && !iconUrl) return null;
    const apps = loadLibraryLocal();
    const idx = apps.findIndex((a) => a.source === trimmed);
    if (idx < 0) return null;
    apps[idx] = {
      ...apps[idx],
      name: name ?? apps[idx].name,
      iconUrl: iconUrl ?? apps[idx].iconUrl,
    };
    saveLibraryLocal(apps);
    return apps;
  } catch {
    return null;
  }
}

/** The saved launch settings for `source`, or null (none / legacy / invalid). */
export function getSavedLaunchBinding(
  source: string,
): SavedLaunchBinding | null {
  const key = source.trim();
  const entry = loadLibrary().find((a) => a.source === key);
  return entry ? normalizeBinding(entry.binding, key) : null;
}

/**
 * Record saved launch settings for `source` (creating the library entry if
 * needed). Called only when a CONFIRMED launch reached ready on a saveable
 * placement. Returns the updated library.
 */
export function recordSavedLaunchBinding(
  source: string,
  binding: SavedLaunchBinding,
): LibraryApp[] {
  const trimmed = source.trim();
  if (!normalizeBinding(binding, trimmed)) return loadLibrary();
  if (isAuthed()) return recordServerSavedLaunchBinding(trimmed, binding);
  return recordSavedLaunchBindingLocal(trimmed, binding);
}

function recordSavedLaunchBindingLocal(
  trimmed: string,
  binding: SavedLaunchBinding,
  key: string = LIBRARY_KEY,
): LibraryApp[] {
  const apps = touchLibraryAppLocal(trimmed, key);
  const idx = apps.findIndex((a) => a.source === trimmed);
  if (idx < 0) return apps;
  apps[idx] = { ...apps[idx], binding };
  saveLibraryLocal(apps, key);
  return apps;
}

function recordServerSavedLaunchBinding(
  trimmed: string,
  binding: SavedLaunchBinding,
): LibraryApp[] {
  if (!isServerResolvable(trimmed)) {
    // Same local-only fallback as `touchServerLibraryApp` (round-1 finding,
    // reviewer-T) — the server library cannot resolve this source shape, so
    // recording the binding server-side would 400 the whole upsert. Reuse
    // the SAME per-account local-fallback key `touchServerLibraryApp` uses
    // (round-2 finding, reviewer-S / reviewer-fresh-r1) — never the
    // shared/unscoped anon-mode key.
    const userId = currentAccountId();
    removeAccountTombstone(userId, trimmed);
    recordSavedLaunchBindingLocal(trimmed, binding, accountLocalKey(userId));
    return loadLibrary();
  }
  touchServerEntryInMemory(trimmed);
  const list = serverEntries ?? [];
  const idx = list.findIndex((a) => a.source === trimmed);
  if (idx >= 0) {
    serverEntries = [
      ...list.slice(0, idx),
      { ...list[idx], binding },
      ...list.slice(idx + 1),
    ];
  }
  // ato-api's `saved_launch_hint.source_key` (services/user_library.ts's
  // `scopedCapsuleIdSchema`) is ALWAYS `publisher/slug`-shaped, with no ULID
  // exception — sending a bare ULID there 400s the WHOLE upsert (library
  // membership included), not just the hint (round-3 finding, reviewer-T /
  // reviewer-fresh-r2). Only include the hint when a valid scoped key is
  // actually available for it (`hintSourceKeyFor`); the plain `source`
  // upsert (membership + touch) below always goes through regardless, since
  // POST /library resolves a bare ULID `source` directly. When no scoped key
  // exists, the binding recorded above (in `serverEntries`) stays correct
  // for the REST OF THIS SESSION — like `invalidateServerSavedLaunchBinding`'s
  // own documented limitation, it just does not persist across reload; it
  // is never silently dropped, only not yet wire-representable.
  const hintSourceKey = hintSourceKeyFor(binding, trimmed);
  const body: Record<string, unknown> = { source: trimmed };
  if (hintSourceKey) {
    // One POST does both the "add + touch" and the hint write — the server
    // treats an upsert body carrying `saved_launch_hint` as setting it in the
    // same call (routes/user_library.ts), so there is no need for the plain
    // touch POST `touchServerLibraryApp` would otherwise fire separately.
    body.saved_launch_hint = toServerHint({
      ...binding,
      sourceKey: hintSourceKey,
    });
  }
  void libraryRequest("/v1/account/library", "POST", body).catch((err) => {
    console.warn("[libraryClient] failed to save launch binding", err);
  });
  return loadLibrary();
}

/** Drop the saved launch settings (library membership is kept). */
export function invalidateSavedLaunchBinding(source: string): LibraryApp[] {
  const trimmed = source.trim();
  if (isAuthed()) {
    // A non-server-resolvable source's binding was recorded through the
    // local fallback above (never present in `serverEntries`) — invalidate
    // it the same way, in this account's own key, then return the merged
    // view for a consistent contract.
    if (!isServerResolvable(trimmed)) {
      invalidateSavedLaunchBindingLocal(
        trimmed,
        accountLocalKey(currentAccountId()),
      );
      return loadLibrary();
    }
    return invalidateServerSavedLaunchBinding(trimmed);
  }
  return invalidateSavedLaunchBindingLocal(trimmed);
}

function invalidateSavedLaunchBindingLocal(
  trimmed: string,
  key: string = LIBRARY_KEY,
): LibraryApp[] {
  const apps = loadLibraryLocal(key);
  const idx = apps.findIndex((a) => a.source === trimmed);
  if (idx >= 0 && apps[idx].binding != null) {
    apps[idx] = { ...apps[idx], binding: null };
    saveLibraryLocal(apps, key);
  }
  return apps;
}

/**
 * In-memory only for the server-backed mode: ato-api#276's `POST /library`
 * treats an OMITTED `saved_launch_hint` as "leave untouched" and an explicit
 * `null` fails `savedLaunchHintSchema` validation (400s the whole call) —
 * there is currently no wire representation of "clear the hint, keep the
 * entry" (confirmed by reading services/user_library.ts directly; this is a
 * real, narrow gap in that already-shipped surface, not a guess). This
 * invalidation is correct for the REST of this session (it is only ever
 * called right after a BINDING_GONE_CODES launch error, so the immediate
 * "don't retry the dead binding" behavior works) but does not persist across
 * reload or another device — a known limitation, not a silent swallow.
 */
function invalidateServerSavedLaunchBinding(trimmed: string): LibraryApp[] {
  const list = serverEntries ?? [];
  const idx = list.findIndex((a) => a.source === trimmed);
  if (idx >= 0 && list[idx].binding != null) {
    serverEntries = [
      ...list.slice(0, idx),
      { ...list[idx], binding: null },
      ...list.slice(idx + 1),
    ];
  }
  return loadLibrary();
}

/** Update only the consent_ref on an existing binding (consent re-approval). */
export function updateBindingConsentRef(
  source: string,
  consentRef: string,
): LibraryApp[] {
  const trimmed = source.trim();
  if (isAuthed()) {
    // Same local-fallback source, same local-fallback update — see
    // `invalidateSavedLaunchBinding`'s matching branch above.
    if (!isServerResolvable(trimmed)) {
      updateBindingConsentRefLocal(
        trimmed,
        consentRef,
        accountLocalKey(currentAccountId()),
      );
      return loadLibrary();
    }
    return updateServerBindingConsentRef(trimmed, consentRef);
  }
  return updateBindingConsentRefLocal(trimmed, consentRef);
}

function updateBindingConsentRefLocal(
  trimmed: string,
  consentRef: string,
  key: string = LIBRARY_KEY,
): LibraryApp[] {
  const apps = loadLibraryLocal(key);
  const idx = apps.findIndex((a) => a.source === trimmed);
  const binding =
    idx >= 0 ? normalizeBinding(apps[idx].binding, trimmed) : null;
  if (idx >= 0 && binding) {
    apps[idx] = { ...apps[idx], binding: { ...binding, consentRef } };
    saveLibraryLocal(apps, key);
  }
  return apps;
}

/** In-memory only, same as `invalidateServerSavedLaunchBinding` — and here
 *  it is also the ONLY correct choice even setting the wire gap aside: the
 *  consent_ref is never sent to the server at all (see `toServerHint`'s doc
 *  comment), so there is nothing to persist server-side regardless. */
function updateServerBindingConsentRef(
  trimmed: string,
  consentRef: string,
): LibraryApp[] {
  const list = serverEntries ?? [];
  const idx = list.findIndex((a) => a.source === trimmed);
  const binding =
    idx >= 0 ? normalizeBinding(list[idx].binding, trimmed) : null;
  if (idx >= 0 && binding) {
    serverEntries = [
      ...list.slice(0, idx),
      { ...list[idx], binding: { ...binding, consentRef } },
      ...list.slice(idx + 1),
    ];
  }
  return loadLibrary();
}
